import {
  BusinessRounded,
  CheckCircleRounded,
  CreditCardRounded,
  FactCheckRounded,
  HandymanRounded,
  PeopleRounded,
  PersonRounded,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { memo, useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  getOverview,
  getRentPayments,
  type OverviewTotals,
  type RentPaymentRecord,
} from "../api/catalog";
import { getTenancies, type TenancyRecord } from "../api/tenancies";
import {
  getMaintenanceIssues,
  type MaintenanceIssueRecord,
} from "../api/maintenance";
import {
  getRoomInspections,
  getKitchenInspections,
  getLaundryInspections,
  type RoomInspectionRecord,
  type KitchenInspectionRecord,
  type LaundryInspectionRecord,
} from "../api/inspections";
import { getProperties, type PropertyRecord } from "../api/properties";
import { getRooms, type RoomRecord } from "../api/rooms";
import { useNotifications } from "../components/NotificationProvider";

const PIE_COLORS = ["#2152b8", "#e53935", "#fb8c00", "#43a047"];

/* ── tiny helpers ───────────────────────────────────────────────── */

function occupancyPercent(occupied: number, total: number) {
  return total === 0 ? 0 : Math.round((occupied / total) * 100);
}

function formatCurrency(n: number) {
  return n.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// helper removed: daysSince not used

/* ── KPI card ───────────────────────────────────────────────────── */

type KpiCardProps = {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
  subtitle?: string;
};

const KpiCard = memo(function KpiCard({
  label,
  value,
  icon,
  accent,
  subtitle,
}: KpiCardProps) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 5,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": { boxShadow: 4, borderColor: accent },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: accent,
        }}
      />
      <CardContent sx={{ pt: 3.5 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              p: 1.25,
              borderRadius: 3,
              bgcolor: `${accent}12`,
              color: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {label}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
});

/* ── section heading ────────────────────────────────────────────── */

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "text.secondary",
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

/* ── occupancy ring (pure SVG — no chart library) ────────────────── */

function OccupancyRing({
  occupied,
  total,
}: {
  occupied: number;
  total: number;
}) {
  const pct = occupancyPercent(occupied, total);
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ - (circ * pct) / 100;
  const theme = useTheme();
  const color =
    pct >= 85
      ? theme.palette.success.main
      : pct >= 50
        ? theme.palette.primary.main
        : theme.palette.warning.main;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        gap: { xs: 2, sm: 3 },
        width: "100%",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 130,
          height: 130,
          flexShrink: 0,
          mx: { xs: "auto", sm: 0 },
        }}
      >
        <svg
          viewBox="0 0 130 130"
          style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}
        >
          <circle
            cx={65}
            cy={65}
            r={r}
            fill="none"
            stroke={theme.palette.action.hover}
            strokeWidth={10}
          />
          <circle
            cx={65}
            cy={65}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={10}
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.8s ease" }}
          />
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
            {pct}%
          </Typography>
        </Box>
      </Box>
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          {occupied} of {total} rooms occupied
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {total - occupied} room{total - occupied !== 1 ? "s" : ""} available
        </Typography>
      </Box>
    </Box>
  );
}

/* ── maintenance donut ──────────────────────────────────────────── */

function MaintenanceDonut({ issues }: { issues: MaintenanceIssueRecord[] }) {
  const byPriority = useMemo(() => {
    const counts: Record<string, number> = {};
    issues
      .filter((i) => i.status === "open" || i.status === "in_progress")
      .forEach((i) => {
        counts[i.priority] = (counts[i.priority] || 0) + 1;
      });
    return [
      { name: "Critical", value: counts["critical"] ?? 0 },
      { name: "High", value: counts["high"] ?? 0 },
      { name: "Medium", value: counts["medium"] ?? 0 },
      { name: "Low", value: counts["low"] ?? 0 },
    ].filter((d) => d.value > 0);
  }, [issues]);

  const total = byPriority.reduce((s, d) => s + d.value, 0);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        gap: { xs: 2, sm: 3 },
        width: "100%",
      }}
    >
      <Box sx={{ width: 130, height: 130, flexShrink: 0 }}>
        {total > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={byPriority}
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={58}
                dataKey="value"
                strokeWidth={0}
              >
                {byPriority.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip
                formatter={(value: any, name: any) => [
                  `${value} issue${value !== 1 ? "s" : ""}`,
                  name,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleRounded
              sx={{ fontSize: 48, color: "success.main", opacity: 0.5 }}
            />
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {total === 0 ? (
          <Typography
            variant="body2"
            color="success.main"
            sx={{ fontWeight: 600 }}
          >
            All clear — no open issues
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {byPriority.map((d, i) => (
              <Stack
                key={d.name}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: PIE_COLORS[i % PIE_COLORS.length],
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                  {d.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {d.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/* ── recent payments list ───────────────────────────────────────── */

function RecentPayments({ payments }: { payments: RentPaymentRecord[] }) {
  const recent = useMemo(
    () =>
      [...payments]
        .sort(
          (a, b) =>
            new Date(b.payment_date).getTime() -
            new Date(a.payment_date).getTime(),
        )
        .slice(0, 5),
    [payments],
  );

  if (recent.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No payments recorded yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {recent.map((p, i) => (
        <Stack
          key={p.id}
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            py: 1.25,
            borderBottom: i < recent.length - 1 ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor:
                p.payment_source === "government"
                  ? "rgba(33,82,184,0.08)"
                  : p.payment_source === "tenant"
                    ? "rgba(15,118,110,0.08)"
                    : "rgba(251,140,0,0.08)",
              color:
                p.payment_source === "government"
                  ? "primary.main"
                  : p.payment_source === "tenant"
                    ? "secondary.main"
                    : "warning.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CreditCardRounded fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(Number(p.amount))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {p.payment_source}
              {p.reference_no ? ` · ${p.reference_no}` : ""}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {new Date(p.payment_date).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            })}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/* ── inspection summary bars ────────────────────────────────────── */

function InspectionBars({
  room,
  kitchen,
  laundry,
}: {
  room: RoomInspectionRecord[];
  kitchen: KitchenInspectionRecord[];
  laundry: LaundryInspectionRecord[];
}) {
  const data = useMemo(() => {
    const build = (items: { result: string }[]) => {
      const pass = items.filter((i) => i.result === "pass").length;
      const fail = items.filter((i) => i.result === "fail").length;
      const attn = items.filter(
        (i) => i.result === "attention_required",
      ).length;
      return { pass, fail, attn, total: items.length };
    };
    return [
      { label: "Room", ...build(room) },
      { label: "Kitchen", ...build(kitchen) },
      { label: "Laundry", ...build(laundry) },
    ];
  }, [room, kitchen, laundry]);

  return (
    <Stack spacing={2}>
      {data.map((d) => (
        <Box key={d.label}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60 }}>
              {d.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {d.total} inspection{d.total !== 1 ? "s" : ""}
            </Typography>
          </Stack>
          {d.total === 0 ? (
            <LinearProgress
              variant="determinate"
              value={0}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "action.hover",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  bgcolor: "text.disabled",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                height: 8,
                borderRadius: 4,
                overflow: "hidden",
                bgcolor: "action.hover",
              }}
            >
              {d.pass > 0 && (
                <Box
                  sx={{
                    width: `${(d.pass / d.total) * 100}%`,
                    bgcolor: "success.main",
                    borderRadius: 4,
                    transition: "width 0.6s ease",
                  }}
                />
              )}
              {d.attn > 0 && (
                <Box
                  sx={{
                    width: `${(d.attn / d.total) * 100}%`,
                    bgcolor: "warning.main",
                    borderRadius: 4,
                    transition: "width 0.6s ease",
                  }}
                />
              )}
              {d.fail > 0 && (
                <Box
                  sx={{
                    width: `${(d.fail / d.total) * 100}%`,
                    bgcolor: "error.main",
                    borderRadius: 4,
                    transition: "width 0.6s ease",
                  }}
                />
              )}
            </Box>
          )}
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
            {d.pass > 0 && (
              <Typography
                variant="caption"
                sx={{ color: "success.main", fontWeight: 600 }}
              >
                {d.pass} pass
              </Typography>
            )}
            {d.attn > 0 && (
              <Typography
                variant="caption"
                sx={{ color: "warning.main", fontWeight: 600 }}
              >
                {d.attn} attention
              </Typography>
            )}
            {d.fail > 0 && (
              <Typography
                variant="caption"
                sx={{ color: "error.main", fontWeight: 600 }}
              >
                {d.fail} fail
              </Typography>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

/* ── property overview rows ─────────────────────────────────────── */

function PropertyOverview({
  properties,
  rooms,
  tenancies,
}: {
  properties: PropertyRecord[];
  rooms: RoomRecord[];
  tenancies: TenancyRecord[];
}) {
  const rows = useMemo(() => {
    const activeTenancyRoomIds = new Set(
      tenancies.filter((t) => t.status === "active").map((t) => t.room_id),
    );
    return properties.map((p) => {
      const propertyRooms = rooms.filter((r) => r.property_id === p.id);
      const occupied = propertyRooms.filter((r) =>
        activeTenancyRoomIds.has(r.id),
      ).length;
      return { ...p, total: propertyRooms.length, occupied };
    });
  }, [properties, rooms, tenancies]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No properties yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {rows.map((p, i) => {
        const pct = occupancyPercent(p.occupied, p.total);
        return (
          <Box
            key={p.id}
            sx={{
              py: 1.5,
              borderBottom: i < rows.length - 1 ? "1px solid" : "none",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.75 }}
            >
              <BusinessRounded sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                noWrap
              >
                {p.property_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {p.occupied}/{p.total}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: "action.hover",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  bgcolor: pct >= 85 ? "success.main" : "primary.main",
                },
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

/* ── main page ──────────────────────────────────────────────────── */

export function DashboardPage() {
  const [overview, setOverview] = useState<OverviewTotals | null>(null);
  const [tenancies, setTenancies] = useState<TenancyRecord[]>([]);
  const [payments, setPayments] = useState<RentPaymentRecord[]>([]);
  const [maintenanceIssues, setMaintenanceIssues] = useState<
    MaintenanceIssueRecord[]
  >([]);
  const [roomInspections, setRoomInspections] = useState<
    RoomInspectionRecord[]
  >([]);
  const [kitchenInspections, setKitchenInspections] = useState<
    KitchenInspectionRecord[]
  >([]);
  const [laundryInspections, setLaundryInspections] = useState<
    LaundryInspectionRecord[]
  >([]);
  const [properties, setProperties] = useState<PropertyRecord[]>([]);
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showAlert } = useNotifications();

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [ov, ten, pay, maint, ri, ki, li, props, rms] = await Promise.all([
        getOverview(),
        getTenancies(),
        getRentPayments(),
        getMaintenanceIssues(),
        getRoomInspections(),
        getKitchenInspections(),
        getLaundryInspections(),
        getProperties(),
        getRooms(),
      ]);
      setOverview(ov.totals);
      setTenancies(ten);
      setPayments(pay);
      setMaintenanceIssues(maint);
      setRoomInspections(ri);
      setKitchenInspections(ki);
      setLaundryInspections(li);
      setProperties(props);
      setRooms(rms);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load dashboard",
        message: "An error occurred while loading summary data.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const t = overview;

  const monthlyIncome = useMemo(() => {
    const active = tenancies.filter((tn) => tn.status === "active");
    return active.reduce((sum, tn) => sum + Number(tn.monthly_rent ?? 0), 0);
  }, [tenancies]);

  const occupiedRooms = useMemo(
    () => tenancies.filter((tn) => tn.status === "active").length,
    [tenancies],
  );

  const totalCollected = useMemo(
    () => payments.reduce((sum, p) => sum + Number(p.amount), 0),
    [payments],
  );

  const totalInspections =
    (t?.roomInspections ?? 0) +
    (t?.kitchenInspections ?? 0) +
    (t?.laundryInspections ?? 0);

  return (
    <Stack spacing={3.5} sx={{ width: "100%", px: 2, position: "relative" }}>
      {isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: -12,
            left: 0,
            right: 0,
            borderRadius: 6,
          }}
        />
      )}

      {/* ── row 1: KPI cards ─────────────────────────── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              sm: "1 1 calc(50% - 12px)",
              md: "1 1 calc(25% - 18px)",
            },
          }}
        >
          <KpiCard
            label="Properties"
            value={t?.properties ?? 0}
            icon={<BusinessRounded />}
            accent="#2152b8"
          />
        </Box>
        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              sm: "1 1 calc(50% - 12px)",
              md: "1 1 calc(25% - 18px)",
            },
          }}
        >
          <KpiCard
            label="Active Tenancies"
            value={t?.tenancies ?? 0}
            icon={<PersonRounded />}
            accent="#0f766e"
            subtitle={`${t?.tenants ?? 0} tenant${(t?.tenants ?? 0) !== 1 ? "s" : ""} total`}
          />
        </Box>
        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              sm: "1 1 calc(50% - 12px)",
              md: "1 1 calc(25% - 18px)",
            },
          }}
        >
          <KpiCard
            label="Open Maintenance"
            value={t?.maintenanceIssues ?? 0}
            icon={<HandymanRounded />}
            accent="#e53935"
            subtitle={`${t?.maintenanceIssues ?? 0} total reported`}
          />
        </Box>
        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              sm: "1 1 calc(50% - 12px)",
              md: "1 1 calc(25% - 18px)",
            },
          }}
        >
          <KpiCard
            label="Inspections"
            value={totalInspections}
            icon={<FactCheckRounded />}
            accent="#fb8c00"
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
          <Card variant="outlined" sx={{ borderRadius: 5, height: "100%" }}>
            <CardContent>
              <SectionTitle>Occupancy</SectionTitle>
              <OccupancyRing occupied={occupiedRooms} total={t?.rooms ?? 0} />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
          <Card variant="outlined" sx={{ borderRadius: 5, height: "100%" }}>
            <CardContent>
              <SectionTitle>Revenue</SectionTitle>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Monthly income potential
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: "secondary.main" }}
                  >
                    {formatCurrency(monthlyIncome)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: "rgba(15,118,110,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <CreditCardRounded sx={{ color: "secondary.main" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total collected
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {formatCurrency(totalCollected)}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: "rgba(33,82,184,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <PeopleRounded sx={{ color: "primary.main" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payments recorded
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {t?.rentPayments ?? 0}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ── row 3: maintenance donut · inspections · payments ──── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
          <Card variant="outlined" sx={{ borderRadius: 5, height: "100%" }}>
            <CardContent>
              <SectionTitle>Maintenance by priority</SectionTitle>
              <MaintenanceDonut issues={maintenanceIssues} />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
          <Card variant="outlined" sx={{ borderRadius: 5, height: "100%" }}>
            <CardContent>
              <SectionTitle>Inspection results</SectionTitle>
              <InspectionBars
                room={roomInspections}
                kitchen={kitchenInspections}
                laundry={laundryInspections}
              />
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(33.33% - 16px)" } }}>
          <Card variant="outlined" sx={{ borderRadius: 5, height: "100%" }}>
            <CardContent>
              <SectionTitle>Recent payments</SectionTitle>
              <RecentPayments payments={payments} />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ── row 4: property portfolio ────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 5 }}>
        <CardContent>
          <SectionTitle>Property portfolio</SectionTitle>
          <PropertyOverview
            properties={properties}
            rooms={rooms}
            tenancies={tenancies}
          />
        </CardContent>
      </Card>
    </Stack>
  );
}
